import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  viewChildren
} from '@angular/core'
import { Router } from '@angular/router'
import { catchError, EMPTY, map, Observable, startWith, take } from 'rxjs'
import { CertificateMetaDataComponent } from '@features/certificate/components/certificate-meta-data/certificate-meta-data.component'
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { ButtonDirective } from 'primeng/button'
import { AlbertSectionNavigationComponent } from '@shared/components/albert-section-navigation/albert-section-navigation.component'
import { DetailviewNavigationService, NavSection } from '@services/detailview-navigation.service'
import { CategoryService } from '@core/auth/services/category.service'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'
import { CERTIFICATE_CATEGORY_OPTIONS } from '@features/template/models/template.model'
import { PermissionService } from '@core/auth/services/permission.service'
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion'
import { CertificateDetailsComponent } from '@features/certificate/components/certificate-details/certificate-details.component'
import { CertificateContractDetailsComponent } from '@features/certificate/components/certificate-contract-details/certificate-contract-details.component'
import { HullSparesCoverProvisionsComponent } from '@features/certificate/components/hull-spares-cover-provisions/hull-spares-cover-provisions.component'
import { LiabilityCoverProvisionsComponent } from '@features/certificate/components/liability-cover-provisions/liability-cover-provisions.component'
import { FinalProvisionsComponent } from '@features/certificate/components/final-provisions/final-provisions.component'
import { CertificateCertificateHolderComponent } from '@features/certificate/components/certificate-ch/certificate-certificate-holder.component'
import { CertificateAcrComponent } from '@features/certificate/components/certificate-acr/certificate-acr.component'
import { CertificateDocumentsComponent } from '@features/certificate/components/certificate-documents/certificate-documents.component'
import { CertificateManagerService } from '@features/certificate/services/certificate-manager/certificate-manager.service'
import { CertificateMetaDataForm } from '@features/certificate/models/certificate-meta-data/certificate-meta-data.model'
import { CertificateDetailsForm } from '@features/certificate/models/certificate-details/certificate-details.model'
import { CertificateContractDetailsForm } from '@features/certificate/models/certificate-contract-details/certificate-contract-details.model'
import { HullSparesCoverProvisionsForm } from '@features/certificate/models/hull-spares-cover-provisions/hull-spares-cover-provisions.model'
import { LiabilityCoverProvisionsForm } from '@features/certificate/models/liability-cover-provisions/liability-cover-provisions.model'
import { FinalProvisionsForm } from '@features/certificate/models/final-provisions/final-provisions.model'
import { PeriodForm } from '@features/insured/models/period/period.model'
import { PolicyForm } from '@features/insured/models/policy/policy.model'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { AlbertTextareaComponent } from '@shared/components/albert-textarea/albert-textarea.component'
import { TableModule } from 'primeng/table'
import { InsuredDataFacade } from '@features/insured/services/insured-data.facade'
import { Divider } from 'primeng/divider'
import { FormAutosaveService } from '@services/form-autosave.service'
import { UnsavedChangesGuard } from '@services/unsaved-changes.guard'
import { ModalHelperService } from '@services/modal-helper.service'
import { GlobalErrorService } from '@services/global-error.service'
import { hasActualValue } from '@core/utils/form-utils'
import { OBJECT_TYPE } from '@features/certificate/models/certificate-select-options.model'

@Component({
  selector: 'albert-certificate',
  providers: [DetailviewNavigationService, CertificateManagerService],
  imports: [
    CertificateMetaDataComponent,
    ReactiveFormsModule,
    ButtonDirective,
    AlbertSectionNavigationComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    CertificateDetailsComponent,
    CertificateContractDetailsComponent,
    HullSparesCoverProvisionsComponent,
    LiabilityCoverProvisionsComponent,
    FinalProvisionsComponent,
    CertificateCertificateHolderComponent,
    CertificateAcrComponent,
    CertificateDocumentsComponent,
    AlbertTextareaComponent,
    TableModule,
    Divider
  ],
  templateUrl: './certificate.component.html',
  styleUrl: '../../shared/styles/detailview.css',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificateComponent implements OnInit, OnDestroy {
  public readonly nav = inject(DetailviewNavigationService)
  public readonly manager = inject(CertificateManagerService)
  public readonly sectionControl = new FormControl<string | null>(null)
  public readonly certificateId = toSignal(
    this.manager.detailsForm.controls.certificateIdValue.valueChanges.pipe(
      startWith(this.manager.detailsForm.controls.certificateIdValue.value),
      map(idValue => idValue?.certificateId ?? null)
    )
  )
  public readonly insuredUuid = toSignal(
    this.manager.metaDataForm.controls.member.valueChanges.pipe(
      startWith(this.manager.metaDataForm.controls.member.value),
      map(member => member?.uuid ?? null)
    ),
    { requireSync: true }
  )
  public readonly periodUuid = toSignal(
    this.manager.periodForm.controls.uuid.valueChanges.pipe(
      startWith(this.manager.periodForm.controls.uuid.value)
    ),
    { requireSync: true }
  )
  public readonly insuredDataFacade = inject(InsuredDataFacade)
  public readonly periodOptions = this.insuredDataFacade.periodOptions
  public readonly policyOptions = this.insuredDataFacade.policyOptions
  public readonly CertificateCategory = CertificateCategory
  // A brand-new, not-yet-saved certificate is protected by the unsaved-changes guard instead.
  public readonly canAutosave = computed(() => this.canEdit() && hasActualValue(this.manager.savedUuid()))
  protected readonly categoryService = inject(CategoryService)
  public readonly activeCategory = computed(() => {
    const activeCategory = this.categoryService.activeCertificateCategory()
    return CERTIFICATE_CATEGORY_OPTIONS.find(c => c.value === activeCategory?.toString()) ?? null
  })
  protected readonly permissionService = inject(PermissionService)
  public readonly canEdit = computed(() => {
    const cat = this.categoryService.activeCategory()
    return cat === null ? false : this.permissionService.canEdit(cat)
  })
  // Form values are no signals - bridge them so `certificateTitle` re-computes on every edit and patch
  private readonly detailsTitle = toSignal(
    this.manager.detailsForm.controls.title.valueChanges.pipe(
      startWith(this.manager.detailsForm.controls.title.value)
    )
  )
  private readonly hullSparesCoverProvisions = toSignal(
    this.manager.hullSparesCoverProvisionsForm.valueChanges.pipe(
      startWith(null),
      map(() => this.manager.hullSparesCoverProvisionsForm.getRawValue())
    ),
    { requireSync: true }
  )
  // Autosave is only allowed once the certificate is persisted (has a uuid) and the user may edit.
  // Lease certificates have no title of their own - they are named after the insured object
  public readonly certificateTitle = computed(() => {
    if (this.categoryService.activeCertificateCategory() === CertificateCategory.LEASE) {
      const hull = this.hullSparesCoverProvisions()
      if (hull.objectType === OBJECT_TYPE.AIRCRAFT) {
        return hull.aircraftRegistration
      }
      if (hull.objectType === OBJECT_TYPE.ENGINE || hull.objectType === OBJECT_TYPE.EQUIPMENT) {
        return hull.equipmentSerialNumber
      }
    }
    return this.detailsTitle()
  })
  private readonly accordionPanels = viewChildren<ElementRef<HTMLElement>>('accordionPanel')
  private readonly sectionHeadings = computed<NavSection[]>(() => {
    const sections = [
      { label: 'Comments', mainIndex: 0 },
      { label: 'Certificate Details', mainIndex: 1 },
      { label: 'Contract Details', mainIndex: 2 },
      { label: 'Hull and Spares Cover & Provisions', mainIndex: 3 },
      { label: 'Liability Cover & Provisions', mainIndex: 4 },
      { label: 'Final Provisions', mainIndex: 5 },
      { label: 'Certificate Holder (CH)', mainIndex: 6 },
      { label: 'Additional Certificate Recipients (ACR)', mainIndex: 7 },
      { label: 'Documents', mainIndex: 8 }
    ]
    return this.categoryService.activeCertificateCategory() === CertificateCategory.LSG
      ? sections.filter(section => section.mainIndex !== 3)
      : sections
  })
  private readonly destroyRef = inject(DestroyRef)
  private readonly router = inject(Router)
  private readonly autosaveService = inject(FormAutosaveService)
  private readonly unsavedGuard = inject(UnsavedChangesGuard)
  private readonly modalHelper = inject(ModalHelperService)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly metaDataFormValid = this.toValiditySignal(this.manager.metaDataForm)
  private readonly certificateDetailsFormValid = this.toValiditySignal(this.manager.detailsForm)
  private readonly periodFormValid = this.toValiditySignal(this.manager.periodForm)
  private readonly policyFormValid = this.toValiditySignal(this.manager.policyForm)
  private readonly contractDetailsFormValid = this.toValiditySignal(this.manager.contractDetailsForm)
  private readonly hullSparesCoverProvisionsFormValid = this.toValiditySignal(this.manager.hullSparesCoverProvisionsForm)
  private readonly liabilityCoverProvisionsFormValid = this.toValiditySignal(this.manager.liabilityCoverProvisionsForm)
  private readonly finalProvisionsFormValid = this.toValiditySignal(this.manager.finalProvisionsForm)
  public readonly isSaveDisabled = computed<boolean>(() => {
    return !this.canEdit()
      || !this.metaDataFormValid()
      || !this.certificateDetailsFormValid()
      || !this.periodFormValid()
      || !this.policyFormValid()
      || !this.contractDetailsFormValid()
      || !this.hullSparesCoverProvisionsFormValid()
      || !this.liabilityCoverProvisionsFormValid()
      || !this.finalProvisionsFormValid()
  })

  constructor() {
    effect(() => {
      this.nav.setSectionHeadings(this.sectionHeadings())
    })
    if (this.isCreateMode()) {
      this.nav.setActivePanels(this.sectionHeadings().map(section => section.mainIndex.toString()))
    }
    afterNextRender(() => {
      this.nav.initialize(this.sectionControl, this.accordionPanels)
    })
    this.sectionControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
      if (val !== null) this.nav.goToSection(Number.parseInt(val, 10))
    })
  }

  public get metaDataForm(): FormGroup<CertificateMetaDataForm> {
    return this.manager.metaDataForm
  }

  public get certificateDetailsForm(): FormGroup<CertificateDetailsForm> {
    return this.manager.detailsForm
  }

  public get contractDetailsForm(): FormGroup<CertificateContractDetailsForm> {
    return this.manager.contractDetailsForm
  }

  public get hullSparesCoverProvisionsForm(): FormGroup<HullSparesCoverProvisionsForm> {
    return this.manager.hullSparesCoverProvisionsForm
  }

  public get liabilityCoversProvisionsForm(): FormGroup<LiabilityCoverProvisionsForm> {
    return this.manager.liabilityCoverProvisionsForm
  }

  public get finalProvisionsForm(): FormGroup<FinalProvisionsForm> {
    return this.manager.finalProvisionsForm
  }

  public get periodForm(): FormGroup<PeriodForm> {
    return this.manager.periodForm
  }

  public get policyForm(): FormGroup<PolicyForm> {
    return this.manager.policyForm
  }

  public isCreateMode(): boolean {
    return this.manager.isCreateMode()
  }

  public onSave(): void {
    if (this.isSaveDisabled()) {
      this.metaDataForm.markAllAsTouched()
      this.certificateDetailsForm.markAllAsTouched()
      this.periodForm.markAllAsTouched()
      this.policyForm.markAsTouched()
      this.contractDetailsForm.markAllAsTouched()
      this.hullSparesCoverProvisionsForm.markAllAsTouched()
      this.liabilityCoversProvisionsForm.markAllAsTouched()
      this.finalProvisionsForm.markAllAsTouched()
      return
    }
    this.manager.performSave$().pipe(take(1)).subscribe(response => {
      if (response.uuid !== null && response.uuid !== undefined && response.uuid !== '') {
        void this.router.navigate(['/certificate', response.uuid], { replaceUrl: true })
      }
    })
  }

  public ngOnInit(): void {
    this.nav.activePanels.set([])
    this.nav.setActivePanels(['1', '2', '3', '4', '5', '6', '7', '8'])

    this.unsavedGuard.register({
      condition: () => !hasActualValue(this.manager.savedUuid()),
      confirmLeave: () => this.confirmLeave()
    })

    this.setupAutosave()
  }

  public ngOnDestroy(): void {
    this.unsavedGuard.unregister()
  }

  private setupAutosave(): void {
    const reportError = () => () => {
      this.globalErrorService.reportError({
        subsystemName: 'Certificate Autosave',
        description: `The data could not be saved automatically.`
      })
      return EMPTY
    }

    // One merged stream over all editable forms → one debounced, consolidated save.
    // Autosave is gated by `canAutosave()` so it never fires for a brand-new (unsaved) certificate
    // or when the user has no edit permission.
    this.autosaveService.observeMany(
      [
        this.metaDataForm,
        this.certificateDetailsForm,
        this.contractDetailsForm,
        this.hullSparesCoverProvisionsForm,
        this.liabilityCoversProvisionsForm,
        this.finalProvisionsForm,
        this.periodForm,
        this.policyForm
      ],
      () => this.manager.performSave$().pipe(catchError(reportError())),
      this.destroyRef,
      {
        extraFilter: () => this.canAutosave(),
        // Certificate holders and additional recipients are lists kept in signals, not forms.
        extraSources: [{
          changes: this.manager.recipientListsEdited$,
          snapshot: () => [this.manager.certificateHolders(), this.manager.additionalCertificateRecipients()]
        }]
      }
    )
  }

  private confirmLeave(): Observable<boolean> {
    return this.modalHelper.confirmLeave(() => this.manager.performSave$())
  }

  private toValiditySignal(control: AbstractControl): Signal<boolean> {
    return toSignal(
      control.statusChanges.pipe(
        startWith(control.status),
        map(() => control.valid)
      ),
      { initialValue: control.valid }
    )
  }
}
